import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';

import { YeehroCalendlyBookingService } from './yeehro-calendly-booking.service';

import { BookMeetingDialogComponent } from '../modules/yeehro-calendly-tools/book-meeting-dialog/book-meeting-dialog.component';
import { BookingAttendeeDialogComponent } from '../modules/yeehro-calendly-tools/booking-attendee-dialog/booking-attendee-dialog.component';
import { BookingInternalDialogComponent } from '../modules/yeehro-calendly-tools/booking-internal-dialog/booking-internal-dialog.component';
import { BookingReassignDialogComponent } from '../modules/yeehro-calendly-tools/booking-reassign-dialog/booking-reassign-dialog.component';
import { OfferTimeSlotsDialogComponent } from '../modules/yeehro-calendly-tools/offer-time-slots-dialog/offer-time-slots-dialog.component';
import { ShareAvailabilityDialogComponent } from '../modules/yeehro-calendly-tools/share-availability-dialog/share-availability-dialog.component';
import { ModuleSearchSelectComponent } from '../modules/module-tools/module-search-select/module-search-select.component';
import { ModuleTypeSelectDialogComponent } from '../modules/module-tools/module-type-select-dialog/module-type-select-dialog.component';

// Centralizes all dialog-opening orchestrators for the Calendly flows.
// Data services (BookingService, InviteTrackerService) stay pure — they only
// own HTTP/CRUD + transformations — and never import dialog components, which
// prevents the cyclic dependency class that bit us earlier (a data service
// importing a dialog that injects the data service back).
//
// Rule that keeps this safe: dialog components opened by this service may NOT
// inject this service. They may inject any data service freely. If a dialog
// needs to spawn another dialog mid-flow, route via an output event up to the
// page component (which CAN inject this service) — matches the pattern used
// throughout the rest of the codebase.
@Injectable({ providedIn: 'root' })
export class YeehroCalendlyDialogService {
  constructor(
    private _dialog: MatDialog,
    private _bookingService: YeehroCalendlyBookingService
  ) {}


  // ── Picker primitives (Lead/Sub/Provider/Contact pickers) ──


  // Opens the event-picker dialog. Dialog handles its own event fetch — defaults
  // to the current user's events, pass listAllEvents=true to show system-wide.
  // Returns the picked event, or null if cancelled.
  async pickEvent(relatedRecord: any = null, listAllEvents: boolean = false): Promise<any> {
    const pickerRef = this._dialog.open(BookMeetingDialogComponent, {
      panelClass: 'yc-dialog-panel',
      autoFocus: false
    });
    const pickerInstance = pickerRef.componentInstance;
    pickerInstance.relatedRecord = relatedRecord;
    pickerInstance.listAllEvents = listAllEvents;

    const picked = await lastValueFrom(pickerRef.afterClosed());
    if (!picked?._id) return null;
    return picked;
  }


  // Opens the module-type picker (Lead/Sub/Provider/Contact). Returns the
  // picked module record, or null if cancelled.
  async pickModuleType(title: string = 'Select Record Type'): Promise<any> {
    const typeRef = this._dialog.open(ModuleTypeSelectDialogComponent, {
      width: '420px',
      panelClass: 'yc-dialog-panel',
      autoFocus: false
    });
    const typeInstance = typeRef.componentInstance;
    typeInstance.acceptedModules = this._bookingService.bookableRecordModules;
    typeInstance.multiSelect = false;
    typeInstance.title = title;

    const picked = await lastValueFrom(typeRef.afterClosed());
    if (!picked?.name) return null;
    return picked;
  }


  // Opens the "who is this meeting with?" dialog. The host either picks a CRM
  // record type (the record picker follows) or types a guest's details for a
  // meeting with someone who isn't in Yeehro. Returns the dialog's choice, or
  // null if cancelled.
  async pickBookingAttendee(title: string = 'Who is this meeting with?'): Promise<any> {
    const attendeeRef = this._dialog.open(BookingAttendeeDialogComponent, {
      panelClass: 'yc-dialog-panel',
      autoFocus: false
    });
    const attendeeInstance = attendeeRef.componentInstance;
    attendeeInstance.title = title;

    const picked = await lastValueFrom(attendeeRef.afterClosed());
    if (!picked?.mode) return null;
    return picked;
  }


  // Resolves the attendee choice into the arguments the booking dialog needs.
  // Record mode runs the record picker; guest mode has everything already, and
  // leaves relatedRecord off so the booking saves with no CRM affiliation (the
  // same shape a standalone public booking has).
  private async _resolveAttendeeSelection(attendee: any): Promise<{ relatedRecord: any; recordType: string; prefill: any } | null> {
    if (attendee?.mode === 'guest') {
      return {
        "relatedRecord": null,
        "recordType": '',
        "prefill": {
          "name": attendee?.guest?.name || '',
          "email": attendee?.guest?.email || '',
          "phone": attendee?.guest?.phone || ''
        }
      };
    }

    const moduleName = attendee?.moduleType?.name;
    if (!moduleName) return null;

    const record = await this.pickRecord(moduleName);
    if (!record?._id) return null;

    return {
      "relatedRecord": record,
      "recordType": moduleName,
      "prefill": this._bookingService.resolveRecordPrefill(record)
    };
  }


  // Opens the record picker for the given module (e.g., 'leads'). Returns the
  // picked record, or null if cancelled or moduleName missing.
  async pickRecord(moduleName: string): Promise<any> {
    if (!moduleName) return null;
    const recordRef = this._dialog.open(ModuleSearchSelectComponent, {
      width: '95%',
      panelClass: 'yc-dialog-panel',
      disableClose: true
    });
    const recordInstance = recordRef.componentInstance;
    recordInstance.fieldToGetRelation = { ref: moduleName };
    recordInstance.multiSelectMode = false;

    const picked = await lastValueFrom(recordRef.afterClosed());
    if (!picked?._id) return null;
    return picked;
  }


  // ── Book meeting flow orchestration ──────────────────────


  // Opens the fullscreen booking-internal dialog with the given event + record context.
  // `prefill` is only passed for a guest booking, where there is no record to derive
  // the invitee fields from; otherwise it comes off the related record as before.
  private async _openBookingDialog(params: { event: any; relatedRecord?: any; recordType?: string; prefill?: any }): Promise<any> {
    const bookingRef = this._dialog.open(BookingInternalDialogComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'yc-fullscreen-dialog',
      autoFocus: false
    });
    const bookingInstance = bookingRef.componentInstance;
    bookingInstance.event = params?.event;
    bookingInstance.relatedRecord = params?.relatedRecord || null;
    bookingInstance.recordType = params?.recordType || '';
    bookingInstance.prefill = params?.prefill || this._bookingService.resolveRecordPrefill(params?.relatedRecord);

    return await lastValueFrom(bookingRef.afterClosed());
  }


  // Record-first: caller has the record (profile widget). Pick event → book.
  async openBookMeetingFlow(relatedRecord: any, recordType: string): Promise<any> {
    const event = await this.pickEvent(relatedRecord);
    if (!event?._id) return null;
    return await this._openBookingDialog({
      "event": event,
      "relatedRecord": relatedRecord,
      "recordType": recordType
    });
  }


  // Event-first: caller has the event (event-list, preview header). Pick attendee
  // (record or guest) → book.
  async openBookMeetingFlowFromEvent(event: any): Promise<any> {
    if (!event?._id) return null;
    const attendee = await this.pickBookingAttendee();
    if (!attendee?.mode) return null;
    const selection = await this._resolveAttendeeSelection(attendee);
    if (!selection) return null;
    return await this._openBookingDialog({
      "event": event,
      "relatedRecord": selection.relatedRecord,
      "recordType": selection.recordType,
      "prefill": selection.prefill
    });
  }


  // From-scratch: nothing known (dashboard CTA). Pick attendee (record or guest)
  // → event → book. Order matches the event-first flow's attendee-then-event
  // sequence so the user experience stays consistent across entry points.
  async openBookMeetingFromScratch(): Promise<any> {
    const attendee = await this.pickBookingAttendee();
    if (!attendee?.mode) return null;
    const selection = await this._resolveAttendeeSelection(attendee);
    if (!selection) return null;
    const event = await this.pickEvent(selection.relatedRecord);
    if (!event?._id) return null;
    return await this._openBookingDialog({
      "event": event,
      "relatedRecord": selection.relatedRecord,
      "recordType": selection.recordType,
      "prefill": selection.prefill
    });
  }


  // Case 1502: stand-in for a meeting whose template is gone completely - its own name, length and
  // location. _id 'none' tells the server to build the rules from the meeting itself.
  private buildEventFromBooking(booking: any): any {
    const minutes = Number(booking?.duration) || Math.round((new Date(booking?.end).getTime() - new Date(booking?.start).getTime()) / 60000) || 30;
    return {
      _id: 'none',
      name: booking?.name || 'Meeting',
      duration: minutes,
      durationOptions: [{ value: minutes, isDefault: true }],
      locations: booking?.location ? [booking.location] : [],
      customQuestions: [],
      schedulingMode: 'one-on-one',
      created_by: booking?.host?._id || booking?.host
    };
  }


  // ── Reschedule flow orchestration ────────────────────────
  //
  // Opens the booking-internal dialog in reschedule mode for an existing
  // booking. Caller passes the booking record (with `event` populated by
  // the auto-populate hook on the booking schema, so it carries the
  // CURRENT event state). Dialog handles its own field updates + reschedule
  // notification on submit; orchestration just opens, awaits, returns.
  //
  // Returns the rescheduled booking record on success, or null when the
  // host cancels.
  async openRescheduleFlow(booking: any): Promise<any> {
    if (!booking?._id) return null;

    const bookingRef = this._dialog.open(BookingInternalDialogComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'yc-fullscreen-dialog',
      autoFocus: false
    });
    const bookingInstance = bookingRef.componentInstance;
    bookingInstance.event = booking?.event || this.buildEventFromBooking(booking);
    bookingInstance.relatedRecord = booking?.relatedRecord || null;
    bookingInstance.recordType = booking?.relatedRecord?.recordType || '';
    // prefill stays null — reschedule mode pulls invitee data from the
    // booking itself (see resolveReschedulePrefill), not the related record.
    bookingInstance.prefill = null;
    bookingInstance.bookingToReschedule = booking;

    return await lastValueFrom(bookingRef.afterClosed());
  }


  // ── Reassign flow orchestration ──────────────────────────
  //
  // Opens the reassign dialog for an existing booking. First-pass wiring: the
  // dialog itself only handles the recruiter pick + optional note and
  // resolves with `{ newHost, note }` on confirm; backend reassign wiring
  // lands in a follow-up chunk. Returns the picker result or null on cancel.
  async openReassignFlow(booking: any): Promise<any> {
    if (!booking?._id) return null;

    const dialogRef = this._dialog.open(BookingReassignDialogComponent, {
      panelClass: 'yc-dialog-panel',
      autoFocus: false
    });
    const instance = dialogRef.componentInstance;
    instance.booking = booking;

    return await lastValueFrom(dialogRef.afterClosed());
  }


  // ── Share Availability flow orchestration ────────────────
  //
  // Opens the Share Availability dialog. If `preselectedEvent` is passed in the
  // options (from the scheduling page event-row action), the dialog skips the
  // event picker and opens at step 2 (compose form) directly. Otherwise the
  // dialog starts at step 1 (event picker) and the host picks an event there.
  //
  // The compose form handles the actual /send HTTP call internally; this method
  // resolves with the saved YeehroCalendlyInviteTracker on success or null if
  // the user cancelled.
  async openShareAvailabilityFlow(options: { relatedRecord?: any; preselectedEvent?: any } = {}): Promise<any> {
    const dialogRef = this._dialog.open(ShareAvailabilityDialogComponent, {
      panelClass: 'yc-dialog-panel',
      autoFocus: false
    });
    const instance = dialogRef.componentInstance;
    instance.preselectedEvent = options?.preselectedEvent || null;
    instance.relatedRecord = options?.relatedRecord || null;

    return await lastValueFrom(dialogRef.afterClosed());
  }


  // From-scratch send-invite (dashboard CTA). Picks module type + record then
  // opens the share-availability dialog. Tacks recordType onto the record so
  // the compose form's tracker linkage resolves to the correct module —
  // matches the widget-activity-feed pattern.
  async openSendInviteFromScratch(): Promise<any> {
    const moduleType = await this.pickModuleType('Select Record Type');
    if (!moduleType?.name) return null;
    const record = await this.pickRecord(moduleType.name);
    if (!record?._id) return null;
    const relatedRecord = { ...record, "recordType": moduleType.name };
    return await this.openShareAvailabilityFlow({ "relatedRecord": relatedRecord });
  }


  // Event-first send-invite (scheduling event-row "Share availability" action).
  // Caller has the event already — picks module type + record, then opens the
  // share-availability dialog with the preselected event so it skips step 1.
  async openSendInviteFlowFromEvent(event: any): Promise<any> {
    if (!event?._id) return null;
    const moduleType = await this.pickModuleType('Select Record Type');
    if (!moduleType?.name) return null;
    const record = await this.pickRecord(moduleType.name);
    if (!record?._id) return null;
    const relatedRecord = { ...record, "recordType": moduleType.name };
    return await this.openShareAvailabilityFlow({
      "preselectedEvent": event,
      "relatedRecord": relatedRecord
    });
  }


  // ── Offer time slots flow ────────────────────────────────
  //
  // Opens the "Offer time slots in email" dialog with the given event. No
  // record/type pickers — this is a loose share flow (no invite tracker,
  // no per-recipient tracking). The dialog handles slot fetch + range
  // picking + multi-format clipboard internally. userSettings carries the
  // host's bookingUsername for building public booking URLs.
  async openOfferTimeSlotsFlow(event: any, userSettings: any): Promise<any> {
    if (!event?._id) return null;
    const dialogRef = this._dialog.open(OfferTimeSlotsDialogComponent, {
      panelClass: 'yc-dialog-panel',
      autoFocus: false
    });
    const instance = dialogRef.componentInstance;
    instance.event = event;
    instance.userSettings = userSettings || null;

    return await lastValueFrom(dialogRef.afterClosed());
  }
}